import Button from "@/components/Button";
import useGtm from "@/hooks/useGTM";
import cloudflareLoader from "@/lib/cloudflare-images";
import { useCountryStripeProduct } from "@/src/features/pricing/hooks/useCountryStripeProduct";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import posthog from "posthog-js";

const PremiumHero = ({
  heading,
  features,
  staticImage,
  staticImageMobile,
  subheading,
  target,
  footerTerms,
  backgroundColour,
  logo,
  minHeight,
  prices,
  getHeadingAndSubheadingFromCms = false,
}) => {
  const { computedSubheading: hookSubheading, computedFooter: hookFooter } =
    useCountryStripeProduct();
  const router = useRouter();

  // TODO: look at making this more scalable so we don't have to do this for every page
  const computedSubheading = getHeadingAndSubheadingFromCms
    ? subheading
    : hookSubheading;
  const computedFooter = getHeadingAndSubheadingFromCms
    ? footerTerms
    : hookFooter;

  const gtm = useGtm();

  const handleAppButtonClick = (platform) => {
    gtm.pushEvent("app_store_cta_clicked", {
      platform,
    });
  };

  const APP_LINK = "https://mob-app.onelink.me/vPUs/uusulqee";

  return (
    <div
      className={clsx(
        "PremiumHero",
        "flex flex-col font-spatial text-gray-900 lg:flex-row"
      )}
      style={{
        minHeight: minHeight ?? "calc(100vh - var(--header-height))",
        background: backgroundColour,
      }}
    >
      <div
        className={clsx(
          "mx-auto w-full flex-1 px-4 py-10 text-center xs:max-w-[555px] sm:px-10 lg:mx-0 lg:w-[50%] lg:max-w-[550px] lg:py-16 lg:text-left xl:max-w-[600px] 2xl:max-w-[700px] 2xl:py-32"
        )}
      >
        <div className={clsx("flex h-full w-full flex-col justify-between")}>
          <div className="flex flex-col items-center gap-6 lg:items-start lg:gap-7 xl:gap-8 2xl:gap-10">
            {logo && logo.length > 0 && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={logo[0].url}
                alt={logo[0].title}
                className="h-4 lg:h-5 xl:h-7"
              />
            )}
            <h1
              className={
                "text-[26px] font-bold leading-none sm:text-[28px] lg:text-[48px] lg:leading-[45px] xl:text-[52px] xl:leading-[46px] 2xl:text-[65px] 2xl:leading-[58px]"
              }
            >
              {heading}
            </h1>
            {computedSubheading && computedSubheading.length && (
              <div className="font-sans text-sm font-medium text-gray-900 xl:text-xl xl:font-extralight">
                {computedSubheading}
              </div>
            )}
            <ul className="flex w-[350px] flex-col gap-3 rounded-2xl border border-gray-900 p-4 lg:w-full lg:border-none lg:p-0">
              {features.split("\n").map((feature) => (
                <li
                  key={feature}
                  className="flex items-center gap-[5px] text-left font-body text-xs font-medium leading-snug lg:text-[15px] lg:font-normal"
                >
                  <Image
                    alt="check"
                    src="/images/check.svg"
                    width="20"
                    height="20"
                    priority
                  />
                  {feature}
                </li>
              ))}
            </ul>
            <div className="flex w-full items-center justify-center space-x-4 lg:justify-start">
              {/*
              TODO: Clean this up when no longer needed
              */}
              {router.asPath.includes("/premium/app") ? (
                <>
                  <Link
                    href={APP_LINK}
                    onClick={() => handleAppButtonClick("apple")}
                  >
                    <Image
                      src="/images/download_on_app_store_wht.png"
                      alt="Download on the app store"
                      width="135"
                      height="40"
                    />
                  </Link>
                  <Link
                    href={APP_LINK}
                    onClick={() => handleAppButtonClick("android")}
                  >
                    <Image
                      src="/images/get_it_on_google_play.svg"
                      alt="Get it on google play"
                      width="135"
                      height="40"
                    />
                  </Link>
                </>
              ) : (
                <Button
                  className="min-w-[187px]"
                  theme="light"
                  href={target.url}
                  onClick={() => {
                    posthog.capture("premium_hero_button_click");
                    return null;
                  }}
                >
                  {target.text}
                </Button>
              )}
            </div>
          </div>
          {computedFooter && computedFooter.length && (
            <div className="mt-6 font-sans text-xs font-normal leading-none text-gray-800 lg:mt-7 xl:mt-8 2xl:mt-10">
              {computedFooter}
            </div>
          )}
        </div>
      </div>
      <div
        className={clsx("w-full overflow-hidden lg:flex lg:flex-1 lg:pt-0", {})}
      >
        {staticImage && staticImage.length === 3 ? (
          <div className="mx-auto xs:max-w-[555px] lg:ml-auto lg:mr-0 lg:w-[40%] lg:min-w-[376px] lg:pl-0 lg:pr-20 lg:pt-10 xl:min-w-[518px] 2xl:min-w-[636px]">
            {staticImage && staticImage.length && (
              <div className="relative grid grid-cols-3 justify-items-center gap-2 overflow-hidden lg:grid-cols-2 lg:justify-items-end lg:gap-5 2xl:gap-6">
                <div
                  className={clsx(
                    "relative z-[1] order-2 col-span-1 h-[168px] w-[168px] md:h-[214px] md:w-[214px] lg:order-1 lg:col-span-2 lg:h-[301px] lg:w-full xl:h-[415px] 2xl:h-[480px]"
                  )}
                >
                  <Image
                    src={staticImage[0].url}
                    fill
                    alt={staticImage[0].title}
                    className="rounded-[13px]"
                    objectFit="cover"
                    priority
                  />
                </div>
                <div
                  className={clsx(
                    "relative order-1 col-span-1 h-[128px] w-[128px] self-end md:h-[154px] md:w-[154px] lg:order-2 lg:h-[128px] lg:w-full xl:h-[176px] 2xl:h-[200px]"
                  )}
                >
                  <Image
                    src={staticImage[1].url}
                    fill
                    alt={staticImage[1].title}
                    className="rounded-[13px]"
                    objectFit="cover"
                    priority
                  />
                </div>
                <div
                  className={clsx(
                    "relative order-3 col-span-1 h-[128px] w-[128px] self-end md:h-[154px] md:w-[154px] lg:order-3 lg:h-[128px] lg:w-full xl:h-[176px] 2xl:h-[200px]"
                  )}
                >
                  <Image
                    src={staticImage[2].url}
                    fill
                    alt={staticImage[2].title}
                    className="rounded-[13px]"
                    objectFit="cover"
                    priority
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="h-full w-full">
            <div className="relative h-full w-full overflow-hidden">
              <div className="absolute left-0 top-0 ml-10 hidden h-full w-full md:block">
                <Image
                  src={staticImage[0].url}
                  alt={staticImage[0].title}
                  className="h-full w-full object-cover"
                  width={1920}
                  height={1920}
                  priority
                  loader={cloudflareLoader}
                  quality={100}
                />
              </div>
            </div>
            <div className="relative block h-full w-full lg:hidden">
              <Image
                src={staticImageMobile?.[0]?.url}
                alt={staticImageMobile?.[0]?.title}
                className="h-full w-full object-cover"
                width={1920}
                height={1920}
                priority
                loader={cloudflareLoader}
                quality={100}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PremiumHero;
